import { useCallback, useEffect, useState } from "react";
import { Check, X, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { card } from "@/components/admin/SettingsPanel";

const KIND = { sermon: "Message", announcement: "Announcement", text: "Website Text", page: "Page" };
const STATUS = { pending: "text-[#8A6A12] bg-[#D4AF37]/15", approved: "text-emerald-700 bg-emerald-100", rejected: "text-[#B4442A] bg-[#F2A08A]/20" };
const FIELDS = [["title_en", "Title"], ["title_mh", "Title (MH)"], ["slug", "Address /p/"], ["intro_en", "Intro"], ["speaker", "Speaker"], ["scripture", "Scripture"], ["body_en", "Message"], ["description_en", "Description"]];

const Preview = ({ p }) => {
  if (!p || !Object.keys(p).length) return null;
  if (p.en || p.mh) {
    const items = [...Object.entries(p.en || {}).map(([k, v]) => [`${k} (EN)`, v]), ...Object.entries(p.mh || {}).map(([k, v]) => [`${k} (MH)`, v])];
    return <ul className="mt-2 space-y-1 text-xs text-[#4A5568]">{items.slice(0, 6).map(([k, v]) => <li key={k}><span className="font-mono">{k}</span>: {String(v).slice(0, 120)}</li>)}</ul>;
  }
  return (
    <ul className="mt-2 space-y-1 text-xs text-[#4A5568]">
      {FIELDS.filter(([k]) => p[k]).map(([k, l]) => <li key={k}><strong>{l}:</strong> {String(p[k]).slice(0, 140)}</li>)}
      {Array.isArray(p.sections) && p.sections.length > 0 && <li><strong>Sections:</strong> {p.sections.map((s) => s.heading_en || "(untitled)").join(", ").slice(0, 160)}</li>}
    </ul>
  );
};

export const ApprovalsPanel = ({ isSuper }) => {
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState({});
  const { refresh } = useSite();
  const load = useCallback(() => api.get("/approvals").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([])), []);
  useEffect(() => {
    load();
  }, [load]);

  const decide = async (r, d) => {
    try {
      await api.post(`/approvals/${r.id}/${d}`, { note: notes[r.id] || "" });
      toast.success(d === "approve" ? "Approved — now live. The editor has been emailed." : "Rejected — the editor has been emailed.");
      load();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="approvals-panel" className="grid gap-3">
      <p className="text-sm text-[#4A5568]">{isSuper ? "Changes from editors wait here until you approve them." : "Your changes that are waiting for (or have had) a Super Admin's review."}</p>
      {rows.map((r) => (
        <div key={r.id} data-testid={`approval-${r.id}`} className={`${card} flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between`}>
          <div className="min-w-0">
            <p className="font-ui text-sm font-semibold text-[#F4EEE2]">{r.summary}</p>
            <p className="mt-0.5 text-xs text-[#9AA4B4]">{KIND[r.kind]} · by {r.user_name} · {new Date(r.at).toLocaleString()}</p>
            <Preview p={r.payload} />
            {r.note && <p className="mt-2 rounded-lg bg-[#D4AF37]/10 px-3 py-2 text-xs text-[#3D4756]"><strong>Note from {r.decided_by}:</strong> {r.note}</p>}
            {isSuper && r.status === "pending" && (
              <textarea data-testid={`approval-note-${r.id}`} rows={2} value={notes[r.id] || ""} onChange={(e) => setNotes((p) => ({ ...p, [r.id]: e.target.value }))} placeholder="Optional note to the editor (sent with your decision)" className="mt-3 w-full rounded-xl border border-[#F4EEE2]/15 bg-[#0B111E] px-3 py-2 text-sm text-[#F4EEE2] outline-none placeholder:text-[#9AA4B4] focus:border-[#D4AF37] sm:min-w-[320px]" />
            )}
          </div>
          {isSuper && r.status === "pending" ? (
            <div className="flex shrink-0 gap-2">
              <button data-testid={`approval-approve-${r.id}`} onClick={() => decide(r, "approve")} className="flex items-center gap-1.5 rounded-full bg-[#D4AF37] px-4 py-2 font-ui text-xs font-semibold text-[#0B111E]"><Check className="h-3.5 w-3.5" /> Approve</button>
              <button data-testid={`approval-reject-${r.id}`} onClick={() => decide(r, "reject")} className="flex items-center gap-1.5 rounded-full border border-[#F4EEE2]/20 px-4 py-2 font-ui text-xs text-[#F2A08A]"><X className="h-3.5 w-3.5" /> Reject</button>
            </div>
          ) : (
            <span className={`w-fit shrink-0 rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${STATUS[r.status]}`}>{r.status}</span>
          )}
        </div>
      ))}
      {!rows.length && <p className="flex items-center justify-center gap-2 py-10 text-sm text-[#4A5568]"><Inbox className="h-4 w-4" /> Nothing waiting for approval.</p>}
    </div>
  );
};
